import { Component, lazy, Suspense, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useStrings,useLocale } from '../../state/useLocale';
import type { IrisBusy } from '../../state/useIris';
import type { IrisClipId } from '../../lib/iris-animation-library';

const IrisCharacter=lazy(()=>import('./IrisCharacter'));
class CharacterBoundary extends Component<{children:ReactNode},{failed:boolean}> {
  state={failed:false};
  static getDerivedStateFromError(){return {failed:true};}
  render(){return this.state.failed?<span className="iris-companion-art" aria-hidden="true"/>:this.props.children;}
}

/** A non-modal conversation: learners can keep working in their terminal.
 * Hide rather than unmount its contents, so closing never loses a draft.
 * The caller keys this on the session to keep drafts out of the next level.
 * `summon` opens it from outside (a stuck nudge's "Start with Iris"): each new
 * value opens it once, as if its own button had been pressed. */
export function IrisCompanion({ children, busy = null, reviewing=false, responseCount=0, feedback=null, summon=0 }: {
  children: ReactNode; busy?: IrisBusy; reviewing?: boolean; responseCount?: number;
  feedback?: {passed:boolean}|null; summon?: number;
}) {
  const s = useStrings().iris;
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(true);
  const [full,setFull]=useState(false),{locale}=useLocale(),composeNoticed=useRef(false);
  const framingLabels:Record<string,readonly[string,string]>={en:['Show full character','Show portrait'],ar:['عرض الشخصية كاملة','عرض الصورة المقربة'],de:['Ganze Figur zeigen','Porträt zeigen'],ja:['全身を表示','ポートレートを表示']},framingText=framingLabels[locale]??framingLabels.en!;
  const [visited, setVisited] = useState(false);
  const [listening,setListening]=useState(false);
  const [reaction,setReaction]=useState<IrisClipId|null>(null);
  const [cue,setCue]=useState(0);
  const [inspectionDone,setInspectionDone]=useState(false);
  const previousFeedback=useRef(feedback);
  const previousResponse=useRef(responseCount);
  const failedChecks=useRef(feedback&&!feedback.passed?1:0);
  const id = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const entry = useRef<HTMLButtonElement>(null);
  const thinking = busy === 'thinking';
  const status = thinking ? s.thinking : busy === 'running' ? s.running : busy === 'checking' ? s.checking : reviewing ? s.companion.reviewing : s.companion.ready;

  function show(trigger: HTMLElement) {
    opener.current = trigger;
    setVisited(true);
    setOpen(true);
    if(!visited&&!busy&&!reviewing&&responseCount===0)react('greeting');
  }

  function close() {
    setOpen(false);
    setListening(false);setReaction(null);composeNoticed.current=false;
    opener.current?.focus();
  }

  useEffect(() => {
    if (summon > 0 && entry.current) show(entry.current);
    // Only a new summon opens it; `show` is a fresh function every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summon]);

  useEffect(() => {
    // Never focus the composer or an approval button on opening.
    if (open) closeButton.current?.focus();
  }, [open]);
  function react(clip:IrisClipId){
    setReaction(clip);setCue(value=>value+1);
  }
  useEffect(()=>{
    const resultChanged=feedback&&feedback!==previousFeedback.current;
    const responseArrived=responseCount>previousResponse.current;
    previousFeedback.current=feedback;previousResponse.current=responseCount;
    const recovered=failedChecks.current>0;
    if(resultChanged)failedChecks.current=feedback.passed?0:failedChecks.current+1;
    if(!open)return;
    if(resultChanged)react(feedback.passed?(recovered?'celebrating':'success'):(failedChecks.current>1?'caution':'curiosity'));
    else if(responseArrived)react('teaching');
  },[feedback,responseCount,open]);
  useEffect(()=>{setInspectionDone(false);if(busy)setReaction(null);},[busy]);
  const clip:IrisClipId=(busy==='checking'&&!inspectionDone?'investigating':busy)??reaction??(listening?'listening':reviewing?'reviewing':'idle');

  return (
    <>
      <button ref={entry} className="iris-companion-entry" type="button" aria-expanded={open} aria-controls={visited ? id : undefined}
        onClick={(event) => show(event.currentTarget)}>
        <span className="iris-portrait" data-thinking={thinking || undefined} aria-hidden="true" />
        <span><strong>{s.companion.open}</strong><span className="iris-companion-entry-note">{s.companion.invite}</span></span>
        <span aria-hidden="true">↗</span>
      </button>
      {createPortal(
        <>
          <button type="button" className="iris-companion-toggle" aria-label={open ? s.companion.close : s.companion.open}
            aria-expanded={open} aria-controls={visited ? id : undefined}
            onClick={(event) => open ? close() : show(event.currentTarget)}>
            <span className="iris-portrait" data-thinking={thinking || undefined} aria-hidden="true" />
            <span>{s.name}</span>
          </button>
          {visited && (
            <aside id={id} className="iris-companion-panel" hidden={!open} role="dialog" aria-labelledby={`${id}-name`}
              data-engaged={Boolean(busy || responseCount > 0 || reviewing)} data-framing={full?'full':'portrait'} data-compact={compact}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && !event.defaultPrevented) {
                  event.preventDefault();
                  event.stopPropagation();
                  close();
                }
              }}>
              <header className="iris-companion-head">
                <h2 id={`${id}-name`} dir="ltr">{s.name}</h2>
                <button ref={closeButton} type="button" className="btn btn-quiet" onClick={close} aria-label={s.companion.close}>×</button>
              </header>
              <div className="iris-review-tools">
                <span className="iris-companion-status" role="status">{status}</span>
                <button type="button" className="btn btn-quiet" aria-pressed={!compact} onClick={() => setCompact(value => !value)}>
                  {compact ? s.companion.showCharacter : s.companion.compactReview}
                </button>
                <button type="button" className="btn btn-quiet" onClick={close}>{s.companion.viewWorkspace}</button>
              </div>
              <div className="iris-companion-stage" dir="ltr" hidden={compact} data-thinking={thinking || undefined}>
                <div className="iris-companion-figure">
                  <CharacterBoundary><Suspense fallback={<span className="iris-companion-art" aria-hidden="true"/>}>
                    <IrisCharacter active={open && !compact} clip={clip} cue={cue} framing={full?'full':'portrait'} onComplete={(completed,completedCue)=>{
                      if(completed==='investigating'&&busy==='checking')setInspectionDone(true);
                      if(completed===reaction&&completedCue===cue)setReaction(null);
                    }}/>
                  </Suspense></CharacterBoundary>
                </div>
                <div className="iris-companion-welcome" dir={locale==='ar'?'rtl':'ltr'}>
                  <p>{s.companion.invite}</p>
                  <span className="iris-companion-status">{status}</span>
                  <button className="iris-framing-toggle" type="button" aria-pressed={full} onClick={()=>setFull(f=>!f)}>{framingText[full?1:0]}</button>
                </div>
              </div>
              <div className="iris-companion-conversation"
                onFocusCapture={event=>{
                  const composing=(event.target as HTMLElement).matches('textarea,input');
                  if(composing&&!composeNoticed.current){composeNoticed.current=true;if(!busy&&!reaction)react('curiosity');}
                  if(composing&&!busy&&reaction&&reaction!=='curiosity')setReaction(null);
                  setListening(composing);
                }}
                onBlurCapture={event=>{
                  if(!(event.relatedTarget as HTMLElement|null)?.matches?.('textarea,input')){
                    setListening(false);setReaction(current=>current==='curiosity'?null:current);
                  }
                }}>{children}</div>
            </aside>
          )}
        </>, document.body,
      )}
    </>
  );
}
